import { AccessibilitySettings } from '@/contexts/AccessibilityContext';
import { theme } from '@/constants/theme';

export function getScaledFontSize(
  baseSize: number,
  currentFontSize: number
): number {
  const scale = currentFontSize / 16;
  return Math.round(baseSize * scale);
}

export function getAccessibleColor(
  color: string,
  settings: AccessibilitySettings
): string {
  if (settings.grayscale) {
    return convertToGrayscale(color);
  }

  if (settings.contrastMode === 'blackYellow') {
    return '#000000';
  }

  if (settings.contrastMode === 'blackWhite') {
    return '#000000';
  }

  return color;
}

export function getAccessibleBackgroundColor(
  backgroundColor: string,
  settings: AccessibilitySettings
): string {
  if (settings.grayscale) {
    return convertToGrayscale(backgroundColor);
  }

  if (settings.contrastMode === 'blackYellow') {
    return '#FFFF00';
  }

  if (settings.contrastMode === 'blackWhite') {
    return '#FFFFFF';
  }

  return backgroundColor;
}

export function getAccessibleTextColor(
  textColor: string,
  settings: AccessibilitySettings
): string {
  if (settings.grayscale) {
    return convertToGrayscale(textColor);
  }

  if (settings.contrastMode === 'blackYellow') {
    return '#000000';
  }

  if (settings.contrastMode === 'blackWhite') {
    return '#000000';
  }

  return textColor;
}

function convertToGrayscale(color: string): string {
  if (color === theme.colors.primary) return '#808080';
  if (color === theme.colors.text) return '#FFFFFF';
  if (color === theme.colors.textLight) return '#B0B0B0';
  if (color === theme.colors.background) return '#000000';
  if (color === theme.colors.surface) return '#1a1a1a';
  if (color === theme.colors.backgroundLight) return '#2a2a2a';
  if (color === theme.colors.border) return '#404040';
  if (color === theme.colors.secondary) return '#1a1a1a';
  if (color === '#FFFFFF') return '#FFFFFF';
  if (color === '#000000') return '#000000';
  return color;
}

export function getAccessibleFontFamily(
  fontFamily: string,
  settings: AccessibilitySettings
): string {
  if (settings.readableFont) {
    return 'System';
  }
  return fontFamily;
}

export function getHeaderStyles(settings: AccessibilitySettings) {
  if (!settings.highlightHeadings) return {};

  return {
    backgroundColor: settings.grayscale ? '#2a2a2a' : 'rgba(139, 92, 246, 0.1)',
    borderRightWidth: 4,
    borderRightColor: settings.grayscale ? '#808080' : theme.colors.primary,
    paddingRight: 12,
    paddingVertical: 8,
    borderRadius: 4,
  };
}

export function getLinkStyles(settings: AccessibilitySettings) {
  if (!settings.highlightLinks) return {};

  return {
    textDecorationLine: 'underline' as const,
    borderWidth: 2,
    borderColor: settings.grayscale ? '#808080' : theme.colors.primary,
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: settings.grayscale ? '#2a2a2a' : 'rgba(139, 92, 246, 0.15)',
  };
}

export function getButtonStyles(settings: AccessibilitySettings) {
  if (!settings.highlightLinks) return {};

  return {
    borderWidth: 3,
    borderColor: settings.grayscale ? '#FFFFFF' : theme.colors.primary,
  };
}

export function shouldReduceMotion(settings: AccessibilitySettings): boolean {
  return settings.stopAnimations;
}

export function getTextStyles(settings: AccessibilitySettings) {
  return {
    letterSpacing: settings.letterSpacing === 'wide' ? 1.2 : 0,
    lineHeight: settings.lineHeight === 'large' ? 1.8 : undefined,
    textAlign: settings.textAlign === 'default' ? undefined : settings.textAlign,
  };
}
